import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  getMobileValidationError,
} from "../utils/mobileInput";
import {
  emailInputFieldProps,
  getEmailValidationError,
} from "../utils/emailInput";
import {
  node_imageBaseUrl,
  getMergedUserData,
  getUserData,
  setUserData,
} from "./const";
import { postEditUserProfile, isErpApiSuccess, getActiveBranch } from "../erpClient";
import { Modal } from "react-bootstrap";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

function formatProfileRoles(userData) {
  const roles = Array.isArray(userData?.roles)
    ? userData.roles.map((r) =>
        r && typeof r === "object" ? r.name || r.label : r,
      )
    : [];
  const cleaned = roles.filter(Boolean).map(String);
  if (cleaned.length) return cleaned.join(", ");
  return "—";
}

// Avatar color variants
const AVATAR_COLORS = [
  "bg-primary",
  "bg-secondary",
  "bg-info",
  "bg-success",
  "bg-warning",
  "bg-danger",
];

const styles = `
  .hover-opacity-100:hover {
    opacity: 1 !important;
  }
  .transition-opacity {
    transition: opacity 0.3s ease;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

function MyProfile() {
  const { buttonClass } = useButtonTableStyle();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [profileData, setProfileData] = useState({
    name: "",
    mobileNumber: "",
    email: "",
    address: "",
    gender: "",
    role: "",
    branchName: "",
    reg_code: "",
    id: "",
    status: "",
    created_at: "",
  });

  // Helper function to get user initials
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  // Helper function to get avatar color
  const getAvatarColor = (name) => {
    const sum = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
  };

  useEffect(() => {
    const applyUserToProfile = () => {
      const userData = getMergedUserData();
      if (!userData) {
        toast.error("Please login to view your profile");
        navigate("/login");
        return;
      }

      const branch = getActiveBranch();
      setProfileData({
        name: userData.name || "",
        mobileNumber:
          userData.phone || userData.mobile || userData.mobileNumber || "",
        email: userData.email || "",
        address: userData.address || "-",
        gender: userData.gender || "",
        role: formatProfileRoles(userData),
        branchName:
          branch?.name ||
          userData.branch_name ||
          (userData.active_branch_id
            ? `Branch #${userData.active_branch_id}`
            : ""),
        reg_code: userData.reg_code || "",
        id: userData.id ?? userData.ua_id ?? "",
        status: userData.status || "",
        created_at: userData.created_at || "",
      });

      if (userData.img) {
        const imagePath = node_imageBaseUrl + userData.img;
        setProfileImage(imagePath);
      } else {
        setProfileImage(null);
      }
    };

    applyUserToProfile();
    window.addEventListener("erpAuthUpdated", applyUserToProfile);
    return () =>
      window.removeEventListener("erpAuthUpdated", applyUserToProfile);
  }, [navigate]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    const mobileError = getMobileValidationError(profileData.mobileNumber, {
      required: true,
    });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(profileData.email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    try {
      // Create a unique filename if image is selected
      const imageName = selectedImageFile
        ? `${Date.now()}_${selectedImageFile.name}`
        : null;

      const formData = new FormData();

      formData.append("id", profileData.id);
      formData.append("name", profileData.name);
      formData.append("phone", profileData.mobileNumber);
      formData.append("email", profileData.email);
      formData.append("address", profileData.address);
      formData.append("gender", profileData.gender.toLowerCase());
      formData.append("image_name", imageName || ""); // Use image_name for the filename

      // Append image file if selected (only once)
      if (selectedImageFile) {
        formData.append("image", selectedImageFile);
      }

      // Debug: Log the FormData contents
      // console.log("FormData being sent:");
      // for (let [key, value] of formData.entries()) {
      //   console.log(
      //     key,
      //     ":",
      //     typeof value === "object" ? "File object" : value
      //   );
      // }

      const data = await postEditUserProfile(formData);
      if (isErpApiSuccess(data) || data?.message === "radha") {
        const userData = getMergedUserData();

        if (!userData) {
          toast.error("Failed to update profile: user data not found");
          return;
        }

        // Get the image path from response.data.data.img (based on the API response structure)
        const newImgPath = data.data?.img || data.img || userData?.img;

        const updatedUserData = {
          ...userData,
          name: profileData.name,
          phone: profileData.mobileNumber,
          email: profileData.email,
          address: profileData.address,
          gender: profileData.gender,
          img: newImgPath || userData.img, // Keep existing img if newImgPath is empty
        };

        try {
          setUserData(updatedUserData);

          // Verify it was saved
          const savedUserData = getUserData();

          if (savedUserData?.img) {
            // Dispatch custom event to notify Navbar and other components about profile image update
            window.dispatchEvent(new CustomEvent("profileImageUpdated"));
          }
        } catch (error) {
          // Error saving userData to localStorage
        }

        // Update profile image display with the new path from server
        if (newImgPath) {
          const imagePath = node_imageBaseUrl + newImgPath;
          setProfileImage(imagePath);
        }

        toast.success("Profile updated successfully");
        setIsEditing(false);
        setSelectedImageFile(null);
      }
    } catch (error) {
      // More detailed error handling
      let errorMessage = "Failed to update profile";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    const userData = getMergedUserData();
    if (!userData) return;
    const branch = getActiveBranch();
    setProfileData({
      name: userData.name || "",
      mobileNumber:
        userData.phone || userData.mobile || userData.mobileNumber || "",
      email: userData.email || "",
      address: userData.address || "-",
      gender: userData.gender || "",
      role: formatProfileRoles(userData),
      branchName:
        branch?.name ||
        userData.branch_name ||
        (userData.active_branch_id
          ? `Branch #${userData.active_branch_id}`
          : ""),
      reg_code: userData.reg_code || "",
      id: userData.id ?? userData.ua_id ?? "",
      status: userData.status || "",
      created_at: userData.created_at || "",
    });
    // Reset image to original
    if (userData.img) {
      setProfileImage(node_imageBaseUrl + userData.img);
    } else {
      setProfileImage(null);
    }
    setSelectedImageFile(null); // Clear the selected file
    setIsEditing(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: name === "mobileNumber" ? sanitizeMobileInput(value) : value,
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        // Set the preview immediately
        setProfileImage(reader.result);
        // Store the file for later upload
        setSelectedImageFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        {/* Main Profile Card */}
        <div
          className={`card border-0 shadow-sm ${
            buttonClass === "rounded-1" ? "rounded-3" : "rounded-0"
          }`}
        >
          <div className="card-body p-0">
            {/* Profile Header */}
            <div
              className={`bg-white border-bottom ${
                buttonClass === "rounded-1" ? "rounded-top-3" : "rounded-top-0"
              }`}
            >
              <div className="container-fluid p-4">
                {/* Page Header Inside Card */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <h4 className="fw-bold mb-1">My Profile</h4>
                  </div>
                  {!isEditing && (
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleEdit}
                        className={`btn btn-primary d-flex align-items-center gap-2 ${buttonClass}`}
                      >
                        <Icon icon="solar:pen-linear" width="18" height="18" />
                        <span className="d-none d-sm-inline">Edit Profile</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="row align-items-center">
                  <div className="col-auto">
                    <div className="position-relative">
                      <div
                        className={`d-flex align-items-center justify-content-center rounded-circle overflow-hidden position-relative shadow ${getAvatarColor(
                          profileData.name,
                        )}`}
                        style={{
                          width: "100px",
                          height: "100px",
                        }}
                      >
                        {profileImage ? (
                          <img
                            src={profileImage}
                            alt="profile-img"
                            className="w-100 h-100 object-fit-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                              setProfileImage(null);
                            }}
                          />
                        ) : (
                          <div
                            className="text-white d-flex align-items-center justify-content-center w-100 h-100 fw-bold"
                            style={{ fontSize: "2.5rem" }}
                          >
                            {getInitials(profileData.name)}
                          </div>
                        )}
                        {isEditing && (
                          <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-circle opacity-0 hover-opacity-100 transition-opacity"
                            style={{ cursor: "pointer" }}
                          >
                            <label
                              htmlFor="profile-image-upload"
                              className="mb-0"
                              style={{ cursor: "pointer" }}
                            >
                              <input
                                type="file"
                                id="profile-image-upload"
                                className="d-none"
                                accept="image/*"
                                onChange={handleImageUpload}
                              />
                              <Icon
                                icon="solar:camera-bold"
                                width="28"
                                height="28"
                                className="text-white"
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        {isEditing ? (
                          <input
                            type="text"
                            name="name"
                            value={profileData.name}
                            onChange={handleChange}
                            className="form-control form-control-lg fw-bold border-0 bg-transparent p-0 mb-2"
                            style={{ width: "300px" }}
                          />
                        ) : (
                          <h3 className="fw-bold mb-2">{profileData.name}</h3>
                        )}

                        <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                          <span className="badge bg-info-subtle text-info px-3 py-2">
                            {profileData.role || "—"}
                          </span>
                          {profileData.branchName ? (
                            <span className="badge bg-secondary-subtle text-secondary px-3 py-2">
                              {profileData.branchName}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {isEditing && (
                        <div className="d-flex gap-2">
                          <button
                            onClick={handleCancel}
                            className={`btn btn-light border d-flex align-items-center gap-2 ${buttonClass}`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSave}
                            className={`btn btn-primary d-flex align-items-center gap-2 ${buttonClass}`}
                          >
                            Save
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="container-fluid p-4">
              <div className="row g-4">
                {/* Contact Information */}
                <div className="col-lg-6">
                  <div className="profile-section mb-4">
                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 p-2 rounded-2 me-3">
                        <Icon
                          icon="solar:phone-linear"
                          width="18"
                          height="18"
                          className="text-primary"
                        />
                      </div>
                      Contact Information
                    </h6>
                  </div>

                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-floating">
                        {isEditing ? (
                          <input
                            {...mobileInputFieldProps}
                            name="mobileNumber"
                            value={profileData.mobileNumber}
                            onChange={handleChange}
                            className="form-control"
                            id="mobileNumber"
                            placeholder="Mobile Number"
                            pattern="[0-9]{10}"
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            value={profileData.mobileNumber}
                            readOnly
                            id="mobileNumber"
                            placeholder="Mobile Number"
                          />
                        )}
                        <label htmlFor="mobileNumber" className="fw-semibold">
                          Mobile Number
                        </label>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating">
                        {isEditing ? (
                          <input
                            {...emailInputFieldProps}
                            name="email"
                            value={profileData.email}
                            onChange={handleChange}
                            className="form-control"
                            id="email"
                            placeholder="Email Address"
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            value={profileData.email}
                            readOnly
                            id="email"
                            placeholder="Email Address"
                          />
                        )}
                        <label htmlFor="email" className="fw-semibold">
                          Email Address
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="col-lg-6">
                  <div className="profile-section mb-4">
                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                      <div className="bg-success bg-opacity-10 p-2 rounded-2 me-3">
                        <Icon
                          icon="solar:user-linear"
                          width="18"
                          height="18"
                          className="text-success"
                        />
                      </div>
                      Personal Information
                    </h6>
                  </div>

                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-floating">
                        {isEditing ? (
                          <select
                            name="gender"
                            value={profileData.gender}
                            onChange={handleChange}
                            className="form-select"
                            id="gender"
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            value={profileData.gender}
                            readOnly
                            id="gender"
                            placeholder="Gender"
                          />
                        )}
                        <label htmlFor="gender" className="fw-semibold">
                          Gender
                        </label>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          value={profileData.role}
                          readOnly
                          id="role"
                          placeholder="Role"
                        />
                        <label htmlFor="role" className="fw-semibold">
                          Role
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="col-12">
                  <div className="profile-section mb-4">
                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                      <div className="bg-info bg-opacity-10 p-2 rounded-2 me-3">
                        <Icon
                          icon="solar:map-point-linear"
                          width="18"
                          height="18"
                          className="text-info"
                        />
                      </div>
                      Address Information
                    </h6>
                  </div>

                  <div className="form-floating">
                    {isEditing ? (
                      <textarea
                        name="address"
                        value={profileData.address}
                        onChange={handleChange}
                        className="form-control"
                        style={{ height: "100px" }}
                        id="address"
                        placeholder="Address"
                      />
                    ) : (
                      <textarea
                        className="form-control"
                        value={profileData.address}
                        readOnly
                        style={{ height: "100px" }}
                        id="address"
                        placeholder="Address"
                      />
                    )}
                    <label htmlFor="address" className="fw-semibold">
                      Full Address
                    </label>
                  </div>
                </div>

                {/* Action Buttons for Mobile */}
                {isEditing && (
                  <div className="col-12 d-block d-lg-none">
                    <div className="d-flex gap-2 justify-content-end">
                      <button
                        onClick={handleCancel}
                        className={`btn btn-light border flex-fill ${buttonClass}`}
                      >
                        <Icon
                          icon="solar:close-circle-linear"
                          width="16"
                          height="16"
                          className="me-2"
                        />
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        className={`btn btn-primary flex-fill ${buttonClass}`}
                      >
                        <Icon
                          icon="solar:check-circle-linear"
                          width="16"
                          height="16"
                          className="me-2"
                        />
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        /* Professional Profile Page Styles */
        .profile-section {
          position: relative;
          margin-bottom: 1.5rem;
        }
        
        .profile-section h6 {
          color: #495057;
          font-weight: 600;
          text-transform: none;
          letter-spacing: normal;
          font-size: 1rem;
          border-bottom: 2px solid #e9ecef;
          padding-bottom: 0.5rem;
          margin-bottom: 1rem;
        }
        
        .form-floating > .form-control,
        .form-floating > .form-select {
          height: 58px;
          min-height: 58px;
          padding-top: 1.625rem;
          padding-bottom: 0.5rem;
        }

        .form-floating > textarea.form-control {
          height: auto;
          min-height: 110px;
          padding-top: 1.625rem;
        }

        .form-floating > .form-control:focus ~ label,
        .form-floating > .form-control:not(:placeholder-shown) ~ label,
        .form-floating > .form-control:-webkit-autofill ~ label,
        .form-floating > .form-select:focus ~ label,
        .form-floating > .form-select:not([size]):not([multiple]) ~ label {
          opacity: 0.8;
          transform: scale(0.85) translateY(-0.65rem) translateX(0.15rem);
          color: var(--bs-primary);
        }
        
        .form-floating > label {
          transition: all 0.3s ease;
          color: #6c757d;
          padding: 1rem 0.75rem;
          transform-origin: 0 0;
        }
        
        .form-floating > .form-control:focus,
        .form-floating > .form-select:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
        }
        
        .hover-opacity-100:hover {
          opacity: 1 !important;
        }
        
        .transition-opacity {
          transition: opacity 0.3s ease;
        }
        
        .breadcrumb {
          background-color: transparent;
          padding: 0;
          margin-bottom: 0;
        }
        
        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
          color: #6c757d;
        }
        
        .breadcrumb-item.active {
          color: var(--bs-primary);
          font-weight: 500;
        }
        
        .card {
          border: 1px solid #e9ecef;
          transition: all 0.3s ease;
        }
        
        .profile-avatar-container {
          transition: transform 0.3s ease;
        }
        
        .profile-avatar-container:hover {
          transform: scale(1.02);
        }
        
        .btn {
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .btn:hover {
          transform: translateY(-1px);
        }
        
        .badge {
          font-weight: 500;
        }
        
        /* Professional color scheme adjustments */
        .bg-primary.bg-opacity-10 {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        
        .bg-success.bg-opacity-10 {
          background-color: rgba(25, 135, 84, 0.1) !important;
        }
        
        .bg-info.bg-opacity-10 {
          background-color: rgba(13, 202, 240, 0.1) !important;
        }
        
        /* Responsive improvements */
        @media (max-width: 768px) {
          .container-fluid {
            padding-left: 1rem;
            padding-right: 1rem;
          }
          
          .card-body {
            padding: 1.5rem;
          }
          
          .profile-section h6 {
            font-size: 0.9rem;
          }
          
          .btn {
            font-size: 0.875rem;
            padding: 0.5rem 1rem;
          }
        }
        
        @media (max-width: 576px) {
          .d-flex.gap-2 {
            flex-direction: column;
          }
          
          .d-flex.gap-2 .btn {
            width: 100%;
            margin-bottom: 0.5rem;
          }
          
          .form-floating > label {
            font-size: 0.875rem;
          }
          
          h4 {
            font-size: 1.5rem;
          }
          
          h3 {
            font-size: 1.75rem;
          }
        }
        
        /* Clean and professional input styles */
        .form-control:focus,
        .form-select:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.15);
        }
        
        .form-control[readonly] {
          background-color: #f8f9fa;
          border-color: #e9ecef;
          color: #495057;
        }
        
        /* Professional spacing */
        .row.g-4 > * {
          padding-top: 1.5rem;
          padding-bottom: 0;
        }
        
        .row.g-3 > * {
          padding-top: 1rem;
          padding-bottom: 0;
        }
      `}</style>
    </div>
  );
}

export default MyProfile;
